import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, KeyRound, LogOut, Mail, RefreshCw } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString();
}

function browserOf(ua = "") {
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Browser";
}

function deviceOf(ua = "") {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh|Mac OS/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Device";
}

function geoOf(row) {
  const bits = [row.city, row.region, row.country_code].filter(Boolean);
  return bits.length ? bits.join(", ") : (row.country || "Unknown");
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-amber-200/85 font-heading">{label}</span>
        <Icon size={15} className="text-amber-200/60" />
      </div>
      <div className="mt-3 font-heading font-light text-white text-4xl tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

function CountryBars({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200/85 font-heading">{title}</div>
      <div className="mt-4 space-y-2.5">
        {rows.length === 0 && <div className="text-zinc-500 text-[12px]">No visits yet.</div>}
        {rows.map((r) => (
          <div key={r.country_code || "?"} className="flex items-center gap-3">
            <span className="w-14 shrink-0 truncate text-[12px] font-medium text-zinc-200">{(r.country_code || "??").toUpperCase()}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-300/80 to-violet-400/70"
                style={{ width: `${Math.max(6, (r.count / max) * 100)}%` }}
              />
            </div>
            <span className="w-8 shrink-0 text-right text-[12px] text-zinc-400">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeedCard({ title, testid, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200/85 font-heading">{title}</div>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function EmptyNote({ children }) {
  return <div className="text-zinc-500 text-[12px]">{children}</div>;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem("mmg_admin_key") || "");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async (k, silent = false) => {
    if (!k) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { params: { key: k } });
      setStats(data);
      setUpdatedAt(new Date());
      setError("");
      sessionStorage.setItem("mmg_admin_key", k);
    } catch (e) {
      sessionStorage.removeItem("mmg_admin_key");
      setStats(null);
      setError(e?.response?.status === 401 ? "Incorrect admin key." : "Could not load stats. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load with a remembered key, then refresh quietly every 30s
  useEffect(() => { if (key) load(key, true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (!stats) return undefined;
    const id = setInterval(() => load(key, true), 30000);
    return () => clearInterval(id);
  }, [stats, key, load]);

  const signOut = () => {
    sessionStorage.removeItem("mmg_admin_key");
    setKey("");
    setStats(null);
  };

  /* ---------- Gate ---------- */
  if (!stats) {
    return (
      <div className="min-h-screen bg-[#03060f] text-white flex items-center justify-center px-6"
        style={{ background: "radial-gradient(90% 80% at 0% 100%, rgba(167,139,250,0.10) 0%, transparent 55%), radial-gradient(120% 90% at 100% 0%, rgba(238,216,154,0.08) 0%, transparent 55%), #03060f" }}>
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-7" data-testid="admin-gate">
          <Logo size={30} />
          <h1 className="mt-5 font-heading font-light text-2xl tracking-tight">Founder dashboard</h1>
          <p className="mt-1 text-[12.5px] text-zinc-400">Private — deck views, signups, and visitor geography.</p>
          <form className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); load(key); }}>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="w-full rounded-xl border border-white/12 bg-white/[0.05] px-4 py-3 text-[14px] text-white placeholder:text-zinc-500 outline-none focus:border-amber-300/50 transition"
              data-testid="admin-key-input"
              autoFocus
            />
            {error && <div className="text-[12px] text-rose-300" data-testid="admin-error">{error}</div>}
            <button
              type="submit"
              disabled={loading || !key}
              className="w-full rounded-xl bg-amber-300/90 hover:bg-amber-200 text-[#0b1738] font-medium text-[13.5px] py-3 transition disabled:opacity-50"
              data-testid="admin-signin-btn"
            >
              {loading ? "Opening…" : "Open dashboard"}
            </button>
          </form>
          <button onClick={() => navigate("/")} className="mt-4 text-[11.5px] text-zinc-500 hover:text-zinc-300 transition" data-testid="admin-gate-exit">
            ← Back to site
          </button>
        </div>
      </div>
    );
  }

  /* ---------- Dashboard ---------- */
  const { waitlist, sign_ins: signIns, deck_views: deckViews } = stats;
  return (
    <div className="min-h-screen bg-[#03060f] text-white"
      style={{ background: "radial-gradient(90% 80% at 0% 100%, rgba(167,139,250,0.08) 0%, transparent 55%), radial-gradient(120% 90% at 100% 0%, rgba(238,216,154,0.06) 0%, transparent 55%), #03060f" }}
      data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b border-white/[0.07] bg-[#03060f]/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size={26} />
            <div>
              <div className="font-heading text-[15px] tracking-tight">Founder dashboard</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Private · My Mood Groove</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {updatedAt && <span className="hidden sm:inline text-[11px] text-zinc-500">Updated {timeAgo(updatedAt.toISOString())}</span>}
            <button
              onClick={() => load(key)}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.05] hover:bg-white/[0.1] px-3 py-1.5 text-[11.5px] text-white/85 transition"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.05] hover:bg-white/[0.1] px-3 py-1.5 text-[11.5px] text-white/85 transition"
              data-testid="admin-signout-btn"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Mail} label="Waitlist signups" value={waitlist.total} sub="Founding members" testid="stat-waitlist" />
          <StatCard icon={KeyRound} label="Portal sign-ins" value={signIns.total} sub={signIns.by_portal.map((p) => `${p.portal}: ${p.count}`).join(" · ")} testid="stat-signins" />
          <StatCard icon={Eye} label="Deck views" value={deckViews.total} sub="Public /deck link" testid="stat-deckviews" />
        </div>

        {/* Country breakdowns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CountryBars title="Signups by country" rows={waitlist.by_country} testid="countries-waitlist" />
          <CountryBars title="Sign-ins by country" rows={signIns.by_country} testid="countries-signins" />
          <CountryBars title="Deck views by country" rows={deckViews.by_country} testid="countries-deckviews" />
        </div>

        {/* Activity feeds */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <FeedCard title="Latest deck views" testid="feed-deckviews">
            {deckViews.recent.length === 0 && <EmptyNote>No deck views yet — share your /deck link.</EmptyNote>}
            {deckViews.recent.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-b border-white/[0.05] pb-2.5 last:border-0 last:pb-0">
                <div>
                  <div className="text-[12.5px] text-zinc-200">{geoOf(r)}</div>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5">{browserOf(r.user_agent)} · {deviceOf(r.user_agent)}</div>
                </div>
                <span className="shrink-0 text-[10.5px] text-zinc-500">{timeAgo(r.timestamp)}</span>
              </div>
            ))}
          </FeedCard>

          <FeedCard title="Latest signups" testid="feed-signups">
            {waitlist.recent.length === 0 && <EmptyNote>No waitlist signups yet.</EmptyNote>}
            {waitlist.recent.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-b border-white/[0.05] pb-2.5 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <div className="text-[12.5px] text-zinc-200 truncate">{r.email}</div>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5">#{r.position} · {r.country_code || r.country || "—"}</div>
                </div>
                <span className="shrink-0 text-[10.5px] text-zinc-500">{timeAgo(r.created_at)}</span>
              </div>
            ))}
          </FeedCard>

          <FeedCard title="Latest portal sign-ins" testid="feed-signins">
            {signIns.recent.length === 0 && <EmptyNote>No portal activity yet.</EmptyNote>}
            {signIns.recent.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-b border-white/[0.05] pb-2.5 last:border-0 last:pb-0">
                <div>
                  <div className="text-[12.5px] text-zinc-200 flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${r.success ? "bg-teal-300" : "bg-rose-400"}`} />
                    {(r.portal || "portal").replace(/_/g, " ")}
                  </div>
                  <div className="text-[10.5px] text-zinc-500 mt-0.5">{geoOf(r)} · {r.success ? "granted" : "denied"}</div>
                </div>
                <span className="shrink-0 text-[10.5px] text-zinc-500">{timeAgo(r.timestamp)}</span>
              </div>
            ))}
          </FeedCard>
        </div>

        <p className="text-[10.5px] text-zinc-600 pt-2">
          Refreshes automatically every 30 seconds · IPs are never displayed · Preview data is separate from production.
        </p>
      </main>
    </div>
  );
}
